import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Check, ArrowRight, Phone, MessageCircle, Send, Loader2, ShieldCheck } from "lucide-react";
import { SERVICE_PAGES, CONTACT, IMG, SITE } from "@/data/content";
import { Words, Reveal } from "@/lib/motion";
import RevealImage from "@/components/RevealImage";
import Magnetic from "@/components/Magnetic";
import Seo from "@/components/Seo";
import { scrollToId } from "@/components/SmoothScroll";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ServiceForm({ slug, anchor }) {
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
    const [loading, setLoading] = useState(false);
    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const getEndpoint = () => {
        if (slug === "nettoyage-syndrome-diogene-strasbourg") return `${API}/diogene-contact`;
        if (slug === "nettoyage-apres-deces-strasbourg") return `${API}/deces-contact`;
        return `${API}/contact`;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
            toast.error("Merci de remplir les champs obligatoires.");
            return;
        }
        setLoading(true);
        try {
            const endpoint = getEndpoint();
            const isSpecialized = endpoint.includes("diogene-contact") || endpoint.includes("deces-contact");
            if (isSpecialized) {
                const fd = new FormData();
                fd.append("name", form.name);
                fd.append("email", form.email || "");
                fd.append("phone", form.phone);
                fd.append("message", form.message);
                fd.append("situation", "");
                fd.append("surface", "");
                fd.append("photo_paths", "");
                await axios.post(endpoint, fd);
            } else {
                await axios.post(endpoint, { ...form, service: slug, source: slug });
            }
            toast.success("Votre demande confidentielle a bien été envoyée.");
            setForm({ name: "", phone: "", email: "", message: "" });
        } catch {
            toast.error("Une erreur est survenue. Merci de nous appeler directement.");
        } finally {
            setLoading(false);
        }
    };
    const input = "w-full border border-line bg-white px-4 py-3 text-sm text-slate-800 focus:border-navy focus:outline-none focus:ring-1 focus:ring-electric/40 transition-all font-body";
    const label = "block text-xs uppercase tracking-wider text-slate-400 mb-2 font-body";
    return (
        <form id={anchor} className="space-y-5 bg-mist border border-line p-8 md:p-10 scroll-mt-32" onSubmit={submit} data-testid="service-form">
            <h3 className="text-xl font-medium text-navy mb-1 font-display">Demande de devis confidentielle</h3>
            <p className="text-sm text-slate-500 font-body mb-4">Vos informations restent strictement confidentielles.</p>
            <div className="grid md:grid-cols-2 gap-5">
                <div><label className={label}>Nom complet *</label><input required value={form.name} onChange={update("name")} className={input} type="text" data-testid="service-name-input" /></div>
                <div><label className={label}>Téléphone *</label><input required value={form.phone} onChange={update("phone")} className={input} type="tel" data-testid="service-phone-input" /></div>
            </div>
            <div><label className={label}>Email</label><input value={form.email} onChange={update("email")} className={input} type="email" data-testid="service-email-input" /></div>
            <div><label className={label}>Description de la situation *</label><textarea required rows={5} value={form.message} onChange={update("message")} className={input + " resize-none"} data-testid="service-message-input" /></div>
            <p className="text-xs text-slate-400 font-body">Toutes les informations transmises sont strictement confidentielles.</p>
            <button type="submit" disabled={loading} className="w-full bg-navy text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold hover:bg-brand-red transition-colors duration-300 flex items-center justify-center gap-2 font-body disabled:opacity-60" data-testid="service-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Envoi en cours..." : "Envoyer ma demande confidentielle"}
            </button>
        </form>
    );
}

function ListBlock({ kicker, title, items, note, cols = 2 }) {
    return (
        <div>
            {kicker && <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">{kicker}</span></Reveal>}
            <Reveal delay={0.05}><h2 className="text-2xl md:text-3xl font-medium text-navy mt-4 mb-8 font-display">{title}</h2></Reveal>
            <div className={"grid gap-x-8 gap-y-4 " + (cols === 2 ? "md:grid-cols-2" : "")}>
                {items.map((it, i) => (
                    <Reveal key={it} delay={0.06 + i * 0.03} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-electric mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-600 font-body">{it}</span>
                    </Reveal>
                ))}
            </div>
            {note && <Reveal delay={0.1}><p className="text-sm text-slate-400 italic mt-8 font-body">{note}</p></Reveal>}
        </div>
    );
}

export default function ServicePage({ pageKey }) {
    const params = useParams();
    const rm = useReducedMotion();
    const heroRef = useRef(null);
    const key = pageKey || (params.slug?.includes("deces") ? "deces" : "diogene");
    const d = SERVICE_PAGES[key];

    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
    const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

    useEffect(() => {
        document.title = d.metaTitle;
        window.scrollTo(0, 0);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    }, [d]);

    return (
        <main data-testid={`service-page-${key}`}>
            <Seo
                title={d.metaTitle}
                description={d.heroText.slice(0, 165)}
                canonical={`${SITE.url}/${d.slug}`}
                image={d.heroImg}
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        serviceType: d.title.join(" "),
                        name: d.title.join(" "),
                        description: d.heroText,
                        url: `${SITE.url}/${d.slug}`,
                        areaServed: { "@type": "State", name: "Alsace" },
                        provider: {
                            "@type": "LocalBusiness",
                            name: SITE.name,
                            telephone: SITE.phoneIntl,
                            url: SITE.url + "/",
                            image: SITE.url + "/logo-ign.png",
                        },
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: d.faq.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Accueil", item: SITE.url + "/" },
                            { "@type": "ListItem", position: 2, name: d.title.join(" "), item: `${SITE.url}/${d.slug}` },
                        ],
                    },
                ]}
            />
            {/* HERO */}
            <section ref={heroRef} className="relative min-h-[88vh] flex items-center overflow-hidden bg-ink">
                <motion.div className="absolute inset-0 z-0" style={rm ? undefined : { y: heroY, scale: heroScale }}>
                    <img src={d.heroImg} alt={d.title.join(" ")} className="w-full h-full object-cover" />
                </motion.div>
                <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(180deg, rgba(6,21,41,0.82), rgba(6,21,41,0.7) 40%, rgba(6,21,41,0.92))" }} />
                <div className="absolute inset-0 z-[1] pointer-events-none" style={{ background: "radial-gradient(55% 50% at 20% 40%, rgba(46,123,255,0.25), transparent 70%)" }} />
                <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pt-40 pb-24">
                    <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="inline-block text-[11px] uppercase tracking-[0.25em] text-electric-soft font-body mb-6 border border-white/20 px-4 py-1.5">
                        {d.heroKicker}
                    </motion.span>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.1] mb-7 font-display">
                        <span className="block overflow-hidden"><Words text={d.title[0]} delay={0.35} mount /></span>
                        <span className="block overflow-hidden font-semibold"><Words text={d.title[1]} delay={0.5} mount /></span>
                    </h1>
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }} className="text-base md:text-lg text-white/75 leading-relaxed max-w-3xl mb-9 font-body">
                        {d.heroText}
                    </motion.p>
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.6 }} className="flex flex-wrap gap-4">
                        <Magnetic strength={0.3}>
                            <button onClick={() => scrollToId(d.anchor)} className="group inline-flex items-center gap-3 bg-electric text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300" data-testid="service-hero-cta">
                                {d.heroCta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </Magnetic>
                        <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300">
                            <Phone className="w-4 h-4" /> Appeler maintenant
                        </a>
                    </motion.div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-red z-10" />
            </section>

            {/* SITUATIONS */}
            <section className="py-24 bg-white">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <ListBlock kicker="Situations prises en charge" title={d.situationsTitle} items={d.situations} note={d.situationsNote} />
                </div>
            </section>

            {/* PRESTATIONS + image */}
            <section className="py-24 bg-mist">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-start">
                    <ListBlock kicker="Nos prestations" title={d.prestationsTitle} items={d.prestations} note={d.prestationsNote} cols={1} />
                    <Reveal y={40} className="relative lg:sticky lg:top-28">
                        <RevealImage src={d.prestationsImg} alt={d.prestationsTitle} className="h-[520px]" parallax />
                        <div className="absolute -bottom-6 -left-6 bg-navy text-white p-7 hidden md:block">
                            <div className="text-3xl font-light font-display">{d.stat.value}</div>
                            <div className="text-xs uppercase tracking-[0.2em] mt-1 font-body text-electric-soft">{d.stat.label}</div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-24 bg-navy relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(50% 40% at 85% 10%, rgba(46,123,255,0.16), transparent 70%)" }} />
                <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
                    <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">Notre processus</span></Reveal>
                    <Reveal delay={0.05}><h2 className="text-2xl md:text-3xl font-medium text-white mt-4 mb-12 font-display">Comment se déroule l&apos;intervention ?</h2></Reveal>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {d.process.map((p, i) => (
                            <Reveal key={p} delay={i * 0.06} y={30} className="border border-white/10 p-6 hover:border-electric/40 transition-colors duration-300">
                                <span className="block text-3xl font-light text-electric-soft font-display mb-4">{String(i + 1).padStart(2, "0")}</span>
                                <p className="text-sm text-white/75 font-body leading-relaxed">{p}</p>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}><p className="text-sm text-white/50 italic mt-10 font-body max-w-3xl">{d.processNote}</p></Reveal>
                </div>
            </section>

            {/* ENGAGEMENTS */}
            <section className="py-24 bg-white">
                <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
                    <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">Nos engagements</span></Reveal>
                    <Reveal delay={0.05}><h2 className="text-2xl md:text-4xl font-medium text-navy mt-4 mb-6 font-display">{d.engagementsTitle}</h2></Reveal>
                    <Reveal delay={0.1}><p className="text-base text-slate-500 leading-relaxed mb-10 font-body">{d.engagementsText}</p></Reveal>
                    <div className="flex flex-wrap justify-center gap-3">
                        {d.engagements.map((e, i) => (
                            <Reveal key={e} delay={0.1 + i * 0.04}>
                                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-mist border border-line text-navy font-body">
                                    <ShieldCheck className="w-4 h-4 text-electric" /> {e}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* DISINFECTION + image */}
            <section className="py-24 bg-ink relative grain">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div>
                        <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-electric-soft font-body">Désinfection &amp; décontamination</span></Reveal>
                        <Reveal delay={0.05}><h2 className="text-2xl md:text-3xl font-medium text-white mt-4 mb-6 font-display">{d.disinfectTitle}</h2></Reveal>
                        {d.disinfectText.map((t, i) => (
                            <Reveal key={i} delay={0.08 + i * 0.05}><p className="text-sm text-white/65 leading-relaxed mb-4 font-body">{t}</p></Reveal>
                        ))}
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-6">
                            {d.disinfect.map((it, i) => (
                                <Reveal key={it} delay={0.1 + i * 0.03} className="flex items-start gap-2.5">
                                    <Check className="w-4 h-4 text-electric mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-white/70 font-body">{it}</span>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <Reveal y={40}>
                        <RevealImage src={IMG.disinfect} alt="Désinfection professionnelle" className="h-[520px]" parallax />
                    </Reveal>
                </div>
            </section>

            {/* ZONE */}
            <section className="py-24 bg-mist">
                <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
                    <Reveal><span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">Zone d&apos;intervention</span></Reveal>
                    <Reveal delay={0.05}><h2 className="text-2xl md:text-3xl font-medium text-navy mt-4 mb-6 font-display">Intervention à Strasbourg et dans toute l&apos;Alsace</h2></Reveal>
                    <Reveal delay={0.1}><p className="text-base text-slate-500 mb-10 font-body max-w-3xl mx-auto">{d.zoneText}</p></Reveal>
                    <div className="flex flex-wrap justify-center gap-3">
                        {d.cities.map((c, i) => (
                            <Reveal key={c} delay={0.1 + i * 0.03}>
                                <span className="inline-block px-5 py-2.5 rounded-full text-sm font-medium bg-white border border-line text-navy font-body">{c}</span>
                            </Reveal>
                        ))}
                        <span className="inline-block px-5 py-2.5 rounded-full text-sm font-medium text-slate-400 font-body">Et plus largement dans tout le Bas-Rhin et le Haut-Rhin</span>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 bg-white">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <Reveal className="text-center mb-12">
                        <span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">Questions fréquentes</span>
                        <h2 className="text-2xl md:text-3xl font-medium text-navy mt-4 font-display">Tout savoir sur notre intervention</h2>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <Accordion type="single" collapsible className="w-full">
                            {d.faq.map((f, i) => (
                                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                                    <AccordionTrigger className="text-left font-display text-navy text-base">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-sm text-slate-600 font-body leading-relaxed">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* DEVIS FORM */}
            <section className="py-24 bg-mist">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <ServiceForm slug={d.slug} anchor={d.anchor} />
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="py-28 bg-ink relative grain overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 50% 30%, rgba(46,123,255,0.2), transparent 70%)" }} />
                <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
                    <Reveal><h2 className="text-3xl md:text-5xl font-light text-white font-display mb-6">{d.closingTitle}</h2></Reveal>
                    <Reveal delay={0.08}><p className="text-white/70 font-body mb-10 leading-relaxed">{d.closingText}</p></Reveal>
                    <Reveal delay={0.12} className="flex flex-wrap justify-center gap-4">
                        <Magnetic strength={0.35}>
                            <button onClick={() => scrollToId(d.anchor)} className="inline-flex items-center gap-3 bg-electric text-white px-9 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300" style={{ boxShadow: "0 0 34px rgba(46,123,255,0.45)" }}>
                                {d.heroCta} <ArrowRight className="w-4 h-4" />
                            </button>
                        </Magnetic>
                        <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/40 text-white px-9 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300">
                            <MessageCircle className="w-4 h-4" /> Nous contacter
                        </a>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <Link to="/" className="inline-block mt-10 text-sm text-electric-soft hover:text-white transition-colors font-body">← Retour à l&apos;accueil</Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
